import React, { useState, useEffect } from 'react';
import { UserDetail } from '../../types/admin';
import { adminService } from '../../services/adminService';
import { badgeService } from '../../services/badgeService';
import { Badge } from '../../types/badge';
import { cn } from '../../lib/utils';
import { Award } from 'lucide-react';

interface UserDetailsModalProps {
  user: UserDetail;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignBadge, setShowAssignBadge] = useState(false);
  const [availableBadges, setAvailableBadges] = useState<Badge[]>([]);
  const [userBadges, setUserBadges] = useState<Badge[]>([]);
  const [loadingBadges, setLoadingBadges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadBadges();
    }
  }, [isOpen, user.id]);

  const loadBadges = async () => {
    setLoadingBadges(true);
    try {
      const [allBadges, userBadgesData] = await Promise.all([
        badgeService.getAllBadges(),
        badgeService.getUserBadges(user.id)
      ]);
      setAvailableBadges(allBadges);
      setUserBadges(userBadgesData.badges || []);
    } catch (error) {
      console.error('Failed to load badges:', error);
    } finally {
      setLoadingBadges(false);
    }
  };

  const handleAssignBadge = async (badgeId: number) => {
    setIsProcessing(true);
    try {
      await badgeService.assignBadgeToUser({ userId: user.id, badgeId });
      alert('Gán badge thành công');
      await loadBadges();
      onUpdate();
    } catch (error: any) {
      alert(error?.message || 'Gán badge thất bại');
    } finally {
      setIsProcessing(false);
      setShowAssignBadge(false);
    }
  };

  const handleRemoveBadge = async (badgeId: number) => {
    if (!window.confirm('Bạn có chắc muốn gỡ badge này?')) return;
    
    setIsProcessing(true);
    try {
      await badgeService.removeBadgeFromUser({ userId: user.id, badgeId });
      alert('Gỡ badge thành công');
      await loadBadges();
      onUpdate();
    } catch (error: any) {
      alert(error?.message || 'Gỡ badge thất bại');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const handleLock = async () => {
    if (!window.confirm('Bạn có chắc muốn khóa tài khoản này?')) return;
    
    setIsProcessing(true);
    try {
      await adminService.lockUser(user.id);
      alert('Khóa tài khoản thành công');
      onUpdate();
      onClose();
    } catch (error: any) {
      alert(error?.message || 'Khóa tài khoản thất bại');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Bạn có chắc muốn mở khóa tài khoản này?')) return;
    
    setIsProcessing(true);
    try {
      await adminService.unlockUser(user.id);
      alert('Mở khóa tài khoản thành công');
      onUpdate();
      onClose();
    } catch (error: any) {
      alert(error?.message || 'Mở khóa tài khoản thất bại');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      await adminService.deleteUser(user.id);
      alert('Xóa tài khoản thành công');
      onUpdate();
      onClose();
    } catch (error: any) {
      alert(error?.message || 'Xóa tài khoản thất bại');
    } finally {
      setIsProcessing(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Chi tiết người dùng</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.displayName} className="w-full h-full rounded-full object-cover" />
              ) : (
                user.displayName[0]
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{user.displayName}</h3>
              <p className="text-gray-600">@{user.username}</p>
              <div className="flex gap-2 mt-2">
                {user.isActive ? (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Hoạt động
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    Bị khóa
                  </span>
                )}
                {user.roles.includes('ADMIN') && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    Admin
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Số điện thoại</label>
                <p className="text-gray-900">{user.phone || 'Chưa cập nhật'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Giới tính</label>
                <p className="text-gray-900">{user.gender === 'male' ? 'Nam' : user.gender === 'female' ? 'Nữ' : 'Chưa cập nhật'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Ngày sinh</label>
                <p className="text-gray-900">{user.birthday ? new Date(user.birthday).toLocaleDateString('vi-VN') : 'Chưa cập nhật'}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Địa chỉ</label>
                <p className="text-gray-900">{user.location || 'Chưa cập nhật'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Ngày tạo</label>
                <p className="text-gray-900">{new Date(user.createdAt).toLocaleString('vi-VN')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Cập nhật lần cuối</label>
                <p className="text-gray-900">{new Date(user.updatedAt).toLocaleString('vi-VN')}</p>
              </div>
            </div>
          </div>

          {user.bio && (
            <div>
              <label className="text-sm font-medium text-gray-500">Tiểu sử</label>
              <p className="text-gray-900 mt-1">{user.bio}</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{user.totalPosts}</p>
              <p className="text-sm text-gray-600">Bài viết</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{user.totalFriends}</p>
              <p className="text-sm text-gray-600">Bạn bè</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{user.totalGroups}</p>
              <p className="text-sm text-gray-600">Nhóm</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold text-gray-900">Badges</h4>
              <button
                onClick={() => setShowAssignBadge(true)}
                disabled={isProcessing || loadingBadges}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors disabled:opacity-50"
              >
                <Award className="w-4 h-4" />
                Gán Badge
              </button>
            </div>
            {loadingBadges ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : userBadges.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {userBadges.map((badge) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    {badge.iconUrl ? (
                      <img src={badge.iconUrl} alt={badge.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Award className="w-5 h-5 text-indigo-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{badge.name}</p>
                      {badge.description && (
                        <p className="text-xs text-gray-500 truncate">{badge.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveBadge(badge.id)}
                      disabled={isProcessing}
                      className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      title="Gỡ badge"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <Award className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Người dùng chưa có badge nào</p>
              </div>
            )}
          </div>

          {!showDeleteConfirm ? (
            <div className="flex gap-3 pt-4 border-t">
              {user.isActive ? (
                <button
                  onClick={handleLock}
                  disabled={isProcessing || user.roles.includes('ADMIN')}
                  className={cn(
                    "flex-1 py-2 px-4 rounded-lg font-medium transition-colors",
                    "bg-orange-500 text-white hover:bg-orange-600",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  Khóa tài khoản
                </button>
              ) : (
                <button
                  onClick={handleUnlock}
                  disabled={isProcessing}
                  className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors disabled:opacity-50"
                >
                  Mở khóa
                </button>
              )}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isProcessing || user.roles.includes('ADMIN')}
                className={cn(
                  "flex-1 py-2 px-4 rounded-lg font-medium transition-colors",
                  "bg-red-500 text-white hover:bg-red-600",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                Xóa tài khoản
              </button>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium mb-3">
                Bạn có chắc chắn muốn xóa tài khoản này? Hành động này không thể hoàn tác!
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDelete}
                  disabled={isProcessing}
                  className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50"
                >
                  Xác nhận xóa
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isProcessing}
                  className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium transition-colors"
                >
                  Hủy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAssignBadge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-2xl font-bold">Gán Badge cho {user.displayName}</h2>
              <button
                onClick={() => setShowAssignBadge(false)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {loadingBadges ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableBadges.length > 0 ? (
                    availableBadges.map((badge) => {
                      const isAssigned = userBadges.some(ub => ub.id === badge.id);
                      return (
                        <div
                          key={badge.id}
                          className={cn(
                            "flex items-center gap-4 p-4 rounded-lg border-2 transition-all",
                            isAssigned
                              ? "bg-gray-50 border-gray-200 opacity-60"
                              : "bg-white border-gray-200 hover:border-indigo-300 hover:shadow-md cursor-pointer"
                          )}
                          onClick={() => !isAssigned && !isProcessing && handleAssignBadge(badge.id)}
                        >
                          {badge.iconUrl ? (
                            <img src={badge.iconUrl} alt={badge.name} className="w-12 h-12 rounded-full" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                              <Award className="w-6 h-6 text-indigo-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{badge.name}</p>
                              {isAssigned && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  Đã gán
                                </span>
                              )}
                            </div>
                            {badge.description && (
                              <p className="text-sm text-gray-600 mt-1">{badge.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Loại: {badge.type} {badge.isAutoAssignable && '• Tự động gán'}
                            </p>
                          </div>
                          {!isAssigned && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssignBadge(badge.id);
                              }}
                              disabled={isProcessing}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors disabled:opacity-50"
                            >
                              Gán
                            </button>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Không có badge nào</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

